import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { syncAccountBalance, syncAllAccountBalances } from '../lib/accountBalance';
import { logAudit } from '../lib/audit';
import { log, logError, startTimer } from '../lib/logger';
import { trackMetric } from '../lib/metrics';

// ── Global invalidation bus ────────────────────────────────────────────────
// After any transaction mutation, we broadcast this event so ALL hooks
// (dashboard, reports, calendar, etc.) know to refetch fresh data.
const TX_CHANGED = 'transactions:changed';
export const broadcastTransactionChange = () =>
  window.dispatchEvent(new Event(TX_CHANGED));
export const onTransactionChange = (cb: () => void) => {
  window.addEventListener(TX_CHANGED, cb);
  return () => window.removeEventListener(TX_CHANGED, cb);
};

export interface Transaction {
  id: string;
  user_id: string;
  account_id: string | null;
  card_id: string | null;
  payment_method: 'account' | 'card';
  /** Forma real de pagamento para despesas via conta: pix | boleto | ted | doc | debito | dinheiro | saque | outro */
  payment_method_detail: string | null;
  description: string;
  category: string;
  subcategory: string | null;
  type: 'receita' | 'despesa';
  amount: number;
  /** Data de vencimento / competência. NÃO é alterada ao marcar como pago. */
  date: string;
  /** Data real em que o pagamento/recebimento ocorreu. Preenchida ao marcar como pago/recebido. */
  paid_at: string | null;
  status: string;
  notes: string;
  recurrence: string;
  installments: number;
  installment_number: number;
  installment_group_id: string | null;
  transfer_group_id: string | null;
  transaction_subtype: string | null;
  show_in_calendar: boolean;
  /** Favorecido vinculado — UUID referenciando a tabela payees */
  payee_id: string | null;
  /** Snapshot do nome do favorecido no momento do lançamento */
  payee_name: string | null;
  created_at: string;
  updated_at: string;
}

export type TransactionPayload = Omit<
  Transaction,
  'id' | 'user_id' | 'created_at' | 'updated_at'
>;

// Legacy arrays kept for filter UI only (DB is source of truth now)
export const CATEGORIES_DESPESA = [
  'Alimentação', 'Moradia', 'Transporte', 'Saúde', 'Educação',
  'Lazer', 'Vestuário', 'Assinaturas', 'Pets', 'Outros',
];
export const CATEGORIES_RECEITA = [
  'Salário', 'Freelance', 'Investimentos', 'Aluguel Recebido', 'Presente', 'Outros',
];

interface UseTransactionsOptions {
  month?: number;
  year?: number;
  type?: 'receita' | 'despesa' | null;
  status?: string | null;
  limit?: number;
}

export const useTransactions = (options: UseTransactionsOptions = {}) => {
  const { user } = useAuth();
  const { month, year, type, status, limit } = options;
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Security: clear stale data immediately when user changes or logs out ──
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== currentUserId) {
      if (prevUserIdRef.current !== undefined || currentUserId === undefined) {
        setTransactions([]);
        setError(null);
      }
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  const fetchTransactions = useCallback(async () => {
    if (!user) {
      setTransactions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    let query = supabase
      .from('transactions')
      .select('*')
      .eq('user_id', user.id)
      .is('deleted_at', null) // ← CRÍTICO: excluir transações soft-deleted
      .order('date', { ascending: false })
      .order('created_at', { ascending: false });

    if (month !== undefined && year !== undefined) {
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endDate = new Date(year, month, 0).toISOString().split('T')[0];
      query = query.gte('date', startDate).lte('date', endDate);
    }
    if (type) query = query.eq('type', type);
    if (status) query = query.eq('status', status);
    if (limit) query = query.limit(limit);

    const { data, error: err } = await query;
    if (err) {
      setError('Não foi possível carregar os lançamentos. Tente novamente.');
    } else {
      setTransactions((data as Transaction[]) ?? []);
    }
    setLoading(false);
  }, [user, month, year, type, status, limit]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  /** Strip empty strings from UUID fields to avoid Postgres UUID cast errors */
  const sanitizePayload = (values: TransactionPayload): TransactionPayload => ({
    ...values,
    account_id: values.account_id || null,
    card_id: values.card_id || null,
    payment_method_detail: values.payment_method_detail || null,
    installment_group_id: values.installment_group_id || null,
    transfer_group_id: (values as Transaction).transfer_group_id || null,
    transaction_subtype: (values as Transaction).transaction_subtype || null,
    subcategory: values.subcategory || null,
    notes: values.notes ?? '',
    category: values.category || 'Outros',
    status: values.status || 'pendente',
    show_in_calendar: values.show_in_calendar ?? true,
    payee_id: (values as Transaction).payee_id || null,
    payee_name: (values as Transaction).payee_name || null,
    paid_at: (values as Transaction).paid_at || null,
  });

  const createTransaction = async (
    values: TransactionPayload
  ): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Usuário não autenticado.' };
    const elapsed = startTimer();

    log({ flow: 'createTransaction', status: 'start', userId: user.id, entity: 'transaction', context: { type: values.type, category: values.category } });

    // ── Server-side limit validation (cannot be bypassed) ───────────────────
    try {
      const { data: limitData, error: limitErr } = await supabase.functions.invoke(
        'validate-transaction-limit',
        { method: 'POST' },
      );
      if (!limitErr && limitData && !limitData.allowed) {
        trackMetric('plan_limit_hit', { flow: 'createTransaction', success: false });
        log({ flow: 'createTransaction', status: 'warning', userId: user.id, message: 'Limite mensal atingido', context: { current: limitData.current, limit: limitData.limit } });
        return {
          success: false,
          message: limitData.message ?? 'Limite mensal de lançamentos atingido. Faça upgrade para continuar.',
        };
      }
    } catch (limitFnErr) {
      // Edge function error → don't block insert (fail open) to avoid disrupting service
      logError('createTransaction', 'validate-transaction-limit indisponível — prosseguindo', { userId: user.id, error: limitFnErr });
    }
    // ────────────────────────────────────────────────────────────────────────

    const payload = sanitizePayload(values);
    const { error: err } = await supabase
      .from('transactions')
      .insert({ ...payload, user_id: user.id });
    if (err) {
      logError('createTransaction', `Erro ao criar lançamento: ${err.message}`, { userId: user.id, entity: 'transaction', context: { code: err.code }, durationMs: elapsed() });
      trackMetric('create_transaction', { flow: 'createTransaction', success: false, durationMs: elapsed() });
      return { success: false, message: `Erro ao criar lançamento: ${err.message}` };
    }
    log({ flow: 'createTransaction', status: 'success', userId: user.id, entity: 'transaction', message: 'Lançamento criado', context: { type: payload.type }, durationMs: elapsed() });
    trackMetric('create_transaction', { flow: 'createTransaction', success: true, durationMs: elapsed() });
    // Sync account balance (only counts pago/recebido — pending not included)
    await syncAccountBalance(payload.account_id ?? null);
    await fetchTransactions();
    // Notify all other hooks that data changed
    broadcastTransactionChange();
    return { success: true, message: 'Lançamento criado com sucesso!' };
  };

  const createManyTransactions = async (
    records: TransactionPayload[]
  ): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Usuário não autenticado.' };
    const elapsed = startTimer();
    const sanitized = records.map(sanitizePayload);
    log({ flow: 'createManyTransactions', status: 'start', userId: user.id, entity: 'transaction', message: `Inserindo ${sanitized.length} parcelas` });
    const { error: err } = await supabase
      .from('transactions')
      .insert(sanitized.map((r) => ({ ...r, user_id: user.id })));
    if (err) {
      logError('createManyTransactions', `Erro ao criar parcelamento: ${err.message}`, { userId: user.id, context: { count: sanitized.length, code: err.code }, durationMs: elapsed() });
      trackMetric('create_transaction', { flow: 'createManyTransactions', success: false, durationMs: elapsed() });
      return { success: false, message: `Erro ao criar parcelamento: ${err.message}` };
    }
    log({ flow: 'createManyTransactions', status: 'success', userId: user.id, entity: 'transaction', message: `${sanitized.length} parcelas criadas`, durationMs: elapsed() });
    trackMetric('create_transaction', { flow: 'createManyTransactions', success: true, durationMs: elapsed() });
    // Collect all unique account_ids and sync each (recurring may have same account)
    const uniqueAccountIds = [...new Set(sanitized.map(r => r.account_id).filter(Boolean))] as string[];
    await Promise.all(uniqueAccountIds.map(id => syncAccountBalance(id)));
    await fetchTransactions();
    broadcastTransactionChange();
    return { success: true, message: `${records.length} parcela(s) criada(s) com sucesso!` };
  };

  const updateTransaction = async (
    id: string,
    values: Partial<TransactionPayload>
  ): Promise<{ success: boolean; message: string }> => {
    // Fetch current record to get account_id before update
    const { data: existing } = await supabase
      .from('transactions')
      .select('account_id')
      .eq('id', id)
      .maybeSingle();

    const { error: err } = await supabase
      .from('transactions')
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (err) {
      logError('updateTransaction', `Erro ao atualizar lançamento: ${err.message}`, { userId: user?.id, entity: 'transaction', recordId: id, context: { code: err.code } });
      return { success: false, message: `Erro ao atualizar lançamento: ${err.message}` };
    }
    // Sync both old and new account if account changed
    const oldAccountId = existing?.account_id ?? null;
    const newAccountId = (values.account_id as string | null) ?? null;
    await syncAccountBalance(oldAccountId);
    if (newAccountId && newAccountId !== oldAccountId) {
      await syncAccountBalance(newAccountId);
    }
    await fetchTransactions();
    broadcastTransactionChange();
    return { success: true, message: 'Lançamento atualizado com sucesso!' };
  };

  const deleteTransaction = async (id: string): Promise<{ success: boolean; message: string }> => {
    const { data: existing } = await supabase
      .from('transactions')
      .select('account_id, description, amount, type, category')
      .eq('id', id)
      .maybeSingle();

    // Soft-delete: preserva o registro para auditoria e histórico
    const { error: err } = await supabase
      .from('transactions')
      .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');

    if (err) return { success: false, message: 'Erro ao excluir lançamento.' };

    // ── Audit trail: registrar exclusão financeira ──────────────────────────
    logAudit({
      action: 'delete_transaction',
      resource: 'transaction',
      resource_id: id,
      details: {
        description: existing?.description,
        amount: existing?.amount,
        type: existing?.type,
        category: existing?.category,
      },
    });
    log({ flow: 'deleteTransaction', status: 'success', userId: user?.id, entity: 'transaction', recordId: id, message: 'Lançamento excluído (soft delete)' });
    trackMetric('delete_transaction', { flow: 'deleteTransaction', success: true });

    // DB trigger handles recalc, but also call frontend sync as safety net
    await syncAccountBalance(existing?.account_id ?? null);
    await fetchTransactions();
    broadcastTransactionChange();
    return { success: true, message: 'Lançamento excluído.' };
  };

  /**
   * Soft-deletes multiple transactions by ID in a single DB call.
   * After deletion, syncs all affected account balances.
   */
  const deleteManyTransactions = async (
    ids: string[]
  ): Promise<{ success: boolean; message: string }> => {
    if (!user || ids.length === 0)
      return { success: false, message: 'Nenhum lançamento selecionado.' };

    // Collect account_ids BEFORE deletion
    const { data: existing } = await supabase
      .from('transactions')
      .select('account_id')
      .in('id', ids)
      .eq('user_id', user.id);

    // Soft-delete: preserva os registros para auditoria e histórico
    const { error: err } = await supabase
      .from('transactions')
      .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .in('id', ids)
      .eq('user_id', user.id);

    if (err) return { success: false, message: 'Erro ao excluir lançamentos.' };

    // Sync all unique affected accounts
    const uniqueAccountIds = [
      ...new Set(
        (existing ?? [])
          .map((r: { account_id: string | null }) => r.account_id)
          .filter(Boolean) as string[]
      ),
    ];

    if (uniqueAccountIds.length > 0) {
      await Promise.all(uniqueAccountIds.map(id => syncAccountBalance(id)));
    } else {
      await syncAllAccountBalances(user.id);
    }

    await fetchTransactions();
    broadcastTransactionChange();
    return {
      success: true,
      message: `${ids.length} lançamento${ids.length !== 1 ? 's' : ''} excluído${ids.length !== 1 ? 's' : ''}.`,
    };
  };

  const duplicateTransaction = async (t: Transaction): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Usuário não autenticado.' };
    const { error: err } = await supabase.from('transactions').insert({
      user_id: user.id,
      account_id: t.account_id,
      card_id: t.card_id,
      payment_method: t.payment_method ?? 'account',
      description: `${t.description} (cópia)`,
      category: t.category,
      subcategory: t.subcategory ?? null,
      type: t.type,
      amount: t.amount,
      date: new Date().toISOString().split('T')[0],
      status: 'pendente',
      notes: t.notes ?? '',
      recurrence: 'none',
      installments: 1,
      installment_number: 1,
      installment_group_id: null,
    });
    if (err) return { success: false, message: 'Erro ao duplicar lançamento.' };
    await syncAccountBalance(t.account_id ?? null);
    await fetchTransactions();
    broadcastTransactionChange();
    return { success: true, message: 'Lançamento duplicado com sucesso!' };
  };

  const toggleStatus = async (
    id: string,
    current: string,
    txType: 'receita' | 'despesa'
  ): Promise<{ success: boolean; message: string }> => {
    const next = current === 'pago' || current === 'recebido'
      ? 'pendente'
      : txType === 'receita' ? 'recebido' : 'pago';
    const today = new Date().toISOString().split('T')[0];
    const extraFields = (next === 'pago' || next === 'recebido')
      ? { paid_at: today }
      : { paid_at: null };
    return updateTransaction(id, { status: next, ...extraFields });
  };

  // ── Computed totals: exclude 'transferencia' subtype from all totals ─────
  // Transfers affect individual account balances but NOT global income/expense.
  const isRealTransaction = (t: Transaction) => t.transaction_subtype !== 'transferencia';

  const monthlyIncome = transactions
    .filter((t) => t.type === 'receita' && isRealTransaction(t))
    .reduce((s, t) => s + t.amount, 0);

  const monthlyExpenses = transactions
    .filter((t) => t.type === 'despesa' && isRealTransaction(t))
    .reduce((s, t) => s + t.amount, 0);

  // ── Realized (actually paid/received) — excludes transfers ────────────────
  const realizedIncome = transactions
    .filter((t) => t.type === 'receita' && t.status === 'recebido' && isRealTransaction(t))
    .reduce((s, t) => s + t.amount, 0);

  const realizedExpenses = transactions
    .filter((t) => t.type === 'despesa' && t.status === 'pago' && isRealTransaction(t))
    .reduce((s, t) => s + t.amount, 0);

  // ── Pending (not yet paid) — excludes transfers ───────────────────────────
  const pendingExpenses = transactions
    .filter((t) => t.type === 'despesa' && ['pendente', 'vencido', 'atrasado'].includes(t.status) && isRealTransaction(t))
    .reduce((s, t) => s + t.amount, 0);

  const pendingIncome = transactions
    .filter((t) => t.type === 'receita' && ['pendente', 'atrasado'].includes(t.status) && isRealTransaction(t))
    .reduce((s, t) => s + t.amount, 0);

  return {
    transactions,
    loading,
    error,
    monthlyIncome,
    monthlyExpenses,
    realizedIncome,
    realizedExpenses,
    pendingExpenses,
    pendingIncome,
    createTransaction,
    createManyTransactions,
    updateTransaction,
    deleteTransaction,
    deleteManyTransactions,
    duplicateTransaction,
    toggleStatus,
    refetch: fetchTransactions,
  };
};
